/**
 * HeaderSidebar.tsx — R8 #4
 *
 * Left-column ornate side panel replacing the horizontal TopBar.
 * Contains: round badge (big), era icon + label, active player chip,
 * global pollution gauge (vertical), and fullscreen toggle.
 *
 * Position: fixed left:0, top:140px, bottom:80px, width:200px, z:99.
 * (Top 140px = PlayerHUD row height. Bottom 80px = PrimaryActionZone.)
 *
 * HexBoardScaler in App.tsx adjusts its marginLeft to 200px to leave room.
 *
 * VISIBLE WHEN: gamePhase === "playing" (mounted inside gameplay shell)
 * TEST PATH: start game → sidebar appears on left, round badge shows "1"
 */

import type React from "react";
import { useEffect, useState } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { ERA_NAMES, SEAT_COLORS, type SeatIndex, useGameStore } from "../../store/game-store.js";
import styles from "./HeaderSidebar.module.css";

/** CF-1 expansion: scaled 200 → 600 (3× round expansion 10 → 30). */
const POLLUTION_MAX = 600;

// Era icons — one emoji per era thematically.
const ERA_ICONS = ["🏭", "🛢️", "⚛️", "☀️"] as const;

// Era name display: wrap long names for the narrow column.
function formatEraName(name: string): string {
  // "Nuclear & Early Renewables" → two lines
  return name.replace(" & ", "\n& ");
}

// Era-specific accent color for the round badge text-shadow / gaugeFill.
const ERA_GAUGE_COLORS = ["#e8a050", "#c9974a", "#a0c0e0", "#ffd86b"] as const;

// Map era → gauge fill color (redder as pollution builds, tinted with era hue).
function gaugeColor(era: number, fraction: number): string {
  // Blend from era-tint (low) → deep red (high).
  const eraHue = ERA_GAUGE_COLORS[(era - 1) % 4] ?? "#e8a050";
  // Simple interpolation: below 50% use era color, above fade to red.
  if (fraction < 0.5) return eraHue;
  if (fraction < 0.8) return "#ea8050";
  return "#ea5656";
}

function FullscreenButton(): React.ReactElement {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    function onFsChange(): void {
      setIsFullscreen(document.fullscreenElement !== null);
    }
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  function handleClick(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void document.documentElement.requestFullscreen();
    }
  }

  return (
    <button
      type="button"
      className={styles.fullscreenBtn}
      onClick={handleClick}
      data-testid="btn-fullscreen"
      title={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
    >
      {isFullscreen ? "⛶ Exit" : "⛶ Full"}
    </button>
  );
}

export function HeaderSidebar(): React.ReactElement {
  const { t } = useI18n();
  const round = useGameStore((s) => s.round);
  const era = useGameStore((s) => s.era);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const pollution = useGameStore((s) => s.pollution);

  const eraName = ERA_NAMES[era - 1] ?? "Unknown Era";
  const eraIcon = ERA_ICONS[era - 1] ?? "🏭";
  const eraNameClass = styles[`eraName${era}` as keyof typeof styles];
  const eraNameClasses = [styles.eraName, eraNameClass ?? ""].filter(Boolean).join(" ");

  const seatColor = SEAT_COLORS[currentSeat as SeatIndex] ?? "#fff";
  const seatLabel = `P${currentSeat + 1}`;

  const totalPollution = pollution[0] + pollution[1] + pollution[2] + pollution[3];
  const fraction = Math.min(1, totalPollution / POLLUTION_MAX);
  const heightPct = `${Math.round(fraction * 100)}%`;
  const fillColor = gaugeColor(era, fraction);
  const isCritical = fraction >= 0.8;

  const fillClass = [styles.gaugeFill, isCritical ? styles.gaugeCritical : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={styles.sidebar} data-testid="header-sidebar" aria-label="Game status panel">
      {/* Round badge */}
      <div className={styles.roundSection}>
        <div className={styles.roundBadge} data-testid="sidebar-round">
          {round}
        </div>
        <div className={styles.roundLabel}>{t("headerSidebar.round")}</div>
      </div>

      {/* Era */}
      <div className={styles.eraSection}>
        <div className={styles.eraIcon} aria-hidden="true">
          {eraIcon}
        </div>
        <div className={styles.eraNumber}>Era {era}</div>
        <div className={eraNameClasses} data-testid="sidebar-era-name">
          {formatEraName(eraName)}
        </div>
      </div>

      {/* Active player chip */}
      <div className={styles.activeSection}>
        <div className={styles.activeLabel}>{t("headerSidebar.active")}</div>
        <div
          className={styles.playerChip}
          style={{ borderColor: seatColor, color: seatColor }}
          data-testid="sidebar-active-seat"
        >
          <span
            className={styles.playerPip}
            style={{ backgroundColor: seatColor }}
            aria-hidden="true"
          />
          <span className={styles.playerChipLabel}>{seatLabel}</span>
        </div>
      </div>

      {/* Global pollution gauge */}
      <div className={styles.pollutionSection}>
        <div className={styles.pollutionLabel}>{t("headerSidebar.pollution")}</div>
        <div
          className={styles.gaugeTrack}
          aria-label={`Global pollution: ${totalPollution} / ${POLLUTION_MAX}`}
        >
          <div
            className={fillClass}
            style={{ height: heightPct, background: fillColor }}
            data-testid="sidebar-pollution-fill"
          />
        </div>
        <div className={styles.gaugeValue} data-testid="sidebar-pollution-value">
          {totalPollution}
        </div>
        <div className={styles.gaugeMax}>/ {POLLUTION_MAX}</div>
        {isCritical && (
          <div className={styles.gaugeCap} data-testid="sidebar-pollution-critical">
            ⚠ CRITICAL
          </div>
        )}
      </div>

      {/* Fullscreen */}
      <div className={styles.fullscreenSection}>
        <FullscreenButton />
      </div>
    </aside>
  );
}
